import { useState } from "react";
import Joyride, { Step, CallBackProps, STATUS, EVENTS } from "react-joyride";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DashboardTourProps {
  userId: string;
  role: 'manager' | 'view_only';
  run: boolean;
  onComplete: () => void;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function DashboardTour({ userId, role, run, onComplete }: DashboardTourProps) {
  const [stepIndex, setStepIndex] = useState(0);

  // Role-specific tour steps
  const getSteps = (): Step[] => {
    const commonSteps: Step[] = [
      {
        target: '[data-tour="overview-tab"]',
        content: "This is your dashboard overview. You'll see key metrics like active orders, revenue, and recent activity at a glance.",
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="orders-tab"]',
        content: "The Orders tab shows all split payment requests and gift vouchers. You can track payment progress and manage order statuses here.",
        placement: 'bottom',
      },
      {
        target: '[data-tour="insights-tab"]',
        content: "View analytics and customer insights here. Track performance trends and understand your split payment patterns.",
        placement: 'bottom',
      },
      {
        target: '[data-tour="settings-tab"]',
        content: "Access notifications and settings here. You can manage your notification preferences.",
        placement: 'bottom',
      },
    ];

    const managerSteps: Step[] = [
      ...commonSteps.slice(0, 2),
      {
        target: '[data-tour="products-tab"]',
        content: "As a Manager, you can enable or disable split payments for products here. Control which products support group payments.",
        placement: 'bottom',
      },
      ...commonSteps.slice(2),
      {
        target: 'body',
        content: "You have permission to process refunds and manage products. When a split payment is cancelled, you can mark refunds as complete from the Orders > Vouchers section.",
        placement: 'center',
      },
    ];

    const viewerSteps: Step[] = [
      ...commonSteps,
      {
        target: 'body',
        content: "As a Viewer, you can monitor all activity but won't be able to make changes. If you need edit access, contact your store admin.",
        placement: 'center',
      },
    ];

    return role === 'manager' ? managerSteps : viewerSteps;
  };

  const handleCallback = async (data: CallBackProps) => {
    const { status, type, index } = data;

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + 1);
    }

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      // Mark tour as completed in database
      try {
        // Use type assertion to work with new table before types are regenerated
        await (supabase as any)
          .from('team_member_profiles')
          .update({ tour_completed: true })
          .eq('user_id', userId);
        
        if (status === STATUS.FINISHED) {
          toast.success("You're all set! Explore your dashboard.");
        }
      } catch (error) {
        console.error("Error updating tour status:", error);
      }
      
      onComplete();
    }
  };

  const steps = getSteps();

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep
      spotlightClicks
      disableOverlayClose
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: 'hsl(var(--primary))',
          backgroundColor: 'hsl(var(--background))',
          textColor: 'hsl(var(--foreground))',
          arrowColor: 'hsl(var(--background))',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: '12px',
          padding: '16px 20px',
        },
        buttonNext: {
          borderRadius: '8px',
          padding: '8px 16px',
        },
        buttonBack: {
          color: 'hsl(var(--muted-foreground))',
        },
        buttonSkip: {
          color: 'hsl(var(--muted-foreground))',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Got it!',
        next: 'Next',
        skip: 'Skip tour',
      }}
    />
  );
};
